/**
 * Story 034 — Zod schema for the per-user Lyse UI preferences blob
 * persisted under `/api/auth/v1/me/preferences/lyse` (BR-097).
 *
 * The schema is intentionally **lenient** — unknown top-level keys are
 * stripped via `.strip()` (default) so an older client encountering a
 * blob written by a newer client doesn't crash. Story 034 AC9 calls
 * this out as the forward-compat protection.
 */
import { z } from "zod";
import { color } from "@aeriosiq/ui/theme";

/**
 * The bounding-box overlay is drawn on the Analysis canvas, which is a
 * calibrated near-black in BOTH themes (the image is the ground, not the
 * page). So every default below is read from `color.dark` regardless of the
 * user's theme — a light-mode value would be picked for a surface that never
 * appears under it. The settings tab's swatch palette reads from the same
 * `color.dark` set, so a factory default is always one of the offered swatches.
 */
const canvas = color.dark;

export const sortPreferenceSchema = z.enum(["catalogue", "severity", "image"]);
export type SortPreference = z.infer<typeof sortPreferenceSchema>;

export const imageStateSchema = z.enum(["active", "inactive"]);
export type ImageState = z.infer<typeof imageStateSchema>;

export const colorTogglesSchema = z.object({
  aiFindingsColor: z.boolean(),
  defectsColor: z.boolean(),
});

// R58-L-17 / auth@4.7.0 — per-user Analysis Viewer settings.
//   maxZoomPercent — caps drag-rectangle + mouse-wheel zoom (L-16).
//   showDebugOverlay — toggles the developer pixel-coord readout (L-15).
// Defaults sourced from the contract: 500 % cap; debug overlay off.
export const viewerPreferencesSchema = z.object({
  maxZoomPercent: z.number().int().min(100).max(1000),
  showDebugOverlay: z.boolean(),
});
export type ViewerPreferences = z.infer<typeof viewerPreferencesSchema>;

export const VIEWER_PREFS_DEFAULTS: ViewerPreferences = {
  maxZoomPercent: 500,
  showDebugOverlay: false,
};

// ANA-R3-TILE-03 / auth@5.6.0 — per-user Analysis Tiles settings.
//   maxZoomPercent — % cap on per-tile crop magnification (Part A). The
//     tile crop zooms to fit the padded defect, then clamps here, so tiny
//     defects fill the tile up to the cap (never hyper-zoomed). It is a
//     percentage of native resolution, so it behaves consistently across
//     the three tile sizes. Default 1000 %; range [100, 2000].
//   tileSize — grid cell-size preset (Part B): small (current) / medium /
//     large. Default `small`.
// The hand-added auth/v1 `tiles` block (api.auth.gen.ts) is the wire type;
// this zod schema is the runtime parse/default surface, mirroring
// `viewerPreferencesSchema`.
export const tileSizeSchema = z.enum(["small", "medium", "large"]);
export type TileSize = z.infer<typeof tileSizeSchema>;

export const tilePreferencesSchema = z.object({
  maxZoomPercent: z.number().int().min(100).max(2000),
  tileSize: tileSizeSchema,
});
export type TilePreferences = z.infer<typeof tilePreferencesSchema>;

export const TILE_PREFS_DEFAULTS: TilePreferences = {
  maxZoomPercent: 1000,
  tileSize: "small",
};

// SETTINGS-02 / auth@5.4.0 — per-user bounding-box appearance. Styling is
// independent per kind (ai/defect); outline-only (0 fill), 2/4 px strokes,
// label on at 12 px near-white-on-box-at-70 %. NOTE: nested object defaults
// are seeded via `.parse({})` (Zod's `.default({})` does NOT re-apply inner
// field defaults), so the factory blob is fully populated.
//
// The two KIND defaults come from D84's ordered `series` set, which names the
// bounding-box defaults as one of its consumers: two marks that have to stay
// apart from each other and from the ground, with no severity meaning attached
// (a kind is not a grade). Series 3 (info blue) reads as machine-suggested,
// series 4 (attention amber) as human-confirmed — the same reading the previous
// blue/orange pair had, now from the system's own encoding ramp.
//
// The SELECTED default is the accent instead: P3 makes aqua the only hue
// permitted to signal current position, and "which box am I on" is exactly
// that. It is also the one hue the two kind defaults can never collide with.
//
// These are USER PREFERENCES — the schema shape and validation are unchanged,
// only the factory values move. A stored blob keeps whatever hex it holds.
export const AI_BBOX_DEFAULT_COLOR: string = canvas.series[2];
export const DEFECT_BBOX_DEFAULT_COLOR: string = canvas.series[3];
export const SELECTED_BBOX_DEFAULT_COLOR: string = canvas.primary;

export const bboxColorBySchema = z.enum(["kind", "severity"]);
export const bboxLabelWhenSchema = z.enum(["always", "selectedOnly"]);
export const bboxLabelPositionSchema = z.enum(["above", "inside", "auto"]);

export const bboxLabelStyleSchema = z.object({
  show: z.boolean().default(true),
  when: bboxLabelWhenSchema.default("always"),
  fontSize: z.number().int().min(8).max(24).default(12),
  // The label sits on the box colour at 70 % over a photograph, i.e. always on
  // the dark canvas — `text.primary` in the dark palette is the system's
  // near-white and the only neutral that reads there.
  textColor: z.string().default(canvas.text.primary),
  // The literal "box" means "inherit the box's colour" — resolved at render.
  bgColor: z.string().default("box"),
  bgOpacity: z.number().min(0).max(1).default(0.7),
  position: bboxLabelPositionSchema.default("above"),
});
export type BboxLabelStyle = z.infer<typeof bboxLabelStyleSchema>;
const BBOX_LABEL_DEFAULTS: BboxLabelStyle = bboxLabelStyleSchema.parse({});

function makeKindStyleSchema(defaultColor: string) {
  return z.object({
    lineWidth: z.number().int().min(1).max(12).default(2),
    lineWidthActive: z.number().int().min(1).max(16).default(4),
    outline: z.boolean().default(true),
    color: z.string().default(defaultColor),
    colorActive: z.string().default(SELECTED_BBOX_DEFAULT_COLOR),
    colorBy: bboxColorBySchema.default("kind"),
    fillOpacity: z.number().min(0).max(1).default(0),
    fillOpacityActive: z.number().min(0).max(1).default(0.12),
    label: bboxLabelStyleSchema.default(() => BBOX_LABEL_DEFAULTS),
  });
}
export const aiBboxStyleSchema = makeKindStyleSchema(AI_BBOX_DEFAULT_COLOR);
export const defectBboxStyleSchema = makeKindStyleSchema(
  DEFECT_BBOX_DEFAULT_COLOR,
);
export type BboxKindStyle = z.infer<typeof aiBboxStyleSchema>;
const AI_BBOX_DEFAULTS: BboxKindStyle = aiBboxStyleSchema.parse({});
const DEFECT_BBOX_DEFAULTS: BboxKindStyle = defectBboxStyleSchema.parse({});

export const boundingBoxPreferencesSchema = z.object({
  autoHideLabelsWhenDense: z.boolean().default(true),
  ai: aiBboxStyleSchema.default(() => AI_BBOX_DEFAULTS),
  defect: defectBboxStyleSchema.default(() => DEFECT_BBOX_DEFAULTS),
});
export type BoundingBoxPreferences = z.infer<
  typeof boundingBoxPreferencesSchema
>;
export const BBOX_PREFS_DEFAULTS: BoundingBoxPreferences =
  boundingBoxPreferencesSchema.parse({});

// SB6-ANA-FILT-05 / auth@5.5.0 — Global Data Point Filter prefs.
//   globalDataPointFilterCountUnit — what every facet bracket counts.
//   globalDataPointFilter — the PERSISTED 7-axis selection (per user,
//     global — NOT per data-acquisition). Loaded on Analysis entry;
//     never auto-reset; reset only via the explicit "Reset to Default"
//     button. `null`/missing ⇒ the FE applies `status: ["ACTIVE"]` with
//     every other axis empty.
export const dataPointFilterCountUnitSchema = z.enum([
  "dataPoints",
  "annotations",
  "defects",
]);
export type DataPointFilterCountUnit = z.infer<
  typeof dataPointFilterCountUnitSchema
>;

export const globalDataPointFilterSchema = z.object({
  status: z.array(z.string()).default([]),
  reviewState: z.array(z.string()).default([]),
  kind: z.array(z.string()).default([]),
  defectTypeIds: z.array(z.string()).default([]),
  componentIds: z.array(z.string()).default([]),
  location: z.array(z.string()).default([]),
  severity: z.array(z.number().int()).default([]),
});
export type GlobalDataPointFilterPref = z.infer<
  typeof globalDataPointFilterSchema
>;

const lysePreferencesObjectSchema = z.object({
  sortPreference: sortPreferenceSchema.default("catalogue"),
  widgetVisibility: z.record(z.string(), z.boolean()).default({}),
  colorToggles: colorTogglesSchema.default({
    aiFindingsColor: true,
    defectsColor: true,
  }),
  favouriteCatalogueEntries: z
    .record(z.string(), z.array(z.string()))
    .default({}),
  imageStateFilter: z.array(imageStateSchema).default(["active"]),
  layout: z.record(z.string(), z.unknown()).default({}),
  viewer: viewerPreferencesSchema.default(VIEWER_PREFS_DEFAULTS),
  // ANA-R3-TILE-03 / auth@5.6.0 — per-user Tiles settings.
  tiles: tilePreferencesSchema.default(TILE_PREFS_DEFAULTS),
  // SETTINGS-02 / auth@5.4.0 — per-user bounding-box appearance.
  boundingBox: boundingBoxPreferencesSchema.default(() => BBOX_PREFS_DEFAULTS),
  // SB6-ANA-FILT-05 / auth@5.5.0 — Global Data Point Filter.
  globalDataPointFilterCountUnit:
    dataPointFilterCountUnitSchema.default("dataPoints"),
  // Nullable on the wire (first-time = null); we keep it optional here so
  // a missing/null value resolves to "no persisted selection" (the FE
  // then applies the ACTIVE-only default).
  globalDataPointFilter: globalDataPointFilterSchema.nullable().default(null),
});

// Wrap the object's strict shape with a separate type alias so that
// downstream callers see *every* field present (not Partial). The
// individual `.default(...)` calls above guarantee that.
export const lysePreferencesSchema = lysePreferencesObjectSchema;
export type LysePreferences = z.infer<typeof lysePreferencesSchema>;

/**
 * Returns the factory-default blob — what we hand to the rest of the
 * app when the server returns 404 (Story 034 AC1).
 */
export function factoryDefaults(): LysePreferences {
  return lysePreferencesSchema.parse({});
}

/**
 * Lenient parse — strips unknown fields and fills missing ones with
 * factory defaults. Returns the typed shape; callers can rely on every
 * top-level field being present.
 */
export function parseLysePreferences(raw: unknown): LysePreferences {
  const result = lysePreferencesSchema.safeParse(raw);
  if (result.success) return result.data;
  // Garbage on the wire — fall back to defaults rather than crash the
  // analysis page. Console-warn so QA notices in dev builds.
  if (typeof console !== "undefined") {
    console.warn("[lyse-prefs] server blob failed schema; using defaults", {
      issues: result.error.issues,
    });
  }
  return factoryDefaults();
}
